import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  ClipboardCheck, Search, Save, History, ArrowUpDown,
  CheckCircle, AlertTriangle, Minus, Plus
} from 'lucide-react';
import { toast } from 'sonner';

export default function InventarioFisicoPage() {
  const [tab, setTab] = useState('conteo'); // conteo | historial
  const [materiales, setMateriales] = useState([]);
  const [filteredMats, setFilteredMats] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [familiaFilter, setFamiliaFilter] = useState('');
  const [conteos, setConteos] = useState({});
  const [nombre, setNombre] = useState('');
  const [saving, setSaving] = useState(false);
  const [historial, setHistorial] = useState([]);
  const [expandedInv, setExpandedInv] = useState(null);

  const loadMateriales = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (familiaFilter) params.set('familia', familiaFilter);
      const res = await api.get(`/bodega/materiales-para-inventario?${params}`);
      setMateriales(res.data.materiales || []);
    } catch { }
    finally { setLoading(false); }
  }, [familiaFilter]);

  useEffect(() => { if (tab === 'conteo') loadMateriales(); }, [tab, loadMateriales]);

  useEffect(() => {
    if (!search) { setFilteredMats(materiales); return; }
    const q = search.toLowerCase();
    setFilteredMats(materiales.filter(m =>
      (m.descripcion2 || '').toLowerCase().includes(q) || (m.codigo || '').includes(q)
    ));
  }, [search, materiales]);

  const loadHistorial = async () => {
    setLoading(true);
    try {
      const res = await api.get('/bodega/inventarios-fisicos');
      setHistorial(res.data.inventarios || []);
    } catch { }
    finally { setLoading(false); }
  };

  useEffect(() => { if (tab === 'historial') loadHistorial(); }, [tab]);

  const setConteo = (codigo, val) => {
    setConteos(prev => ({ ...prev, [codigo]: val }));
  };

  const itemsConConteo = Object.keys(conteos).filter(k => conteos[k] !== '' && conteos[k] !== undefined);
  const itemsConDiferencia = itemsConConteo.filter(k => {
    const mat = materiales.find(m => m.codigo === k);
    return mat && Number(conteos[k]) !== mat.stock_actual;
  });

  const submitInventario = async () => {
    if (itemsConConteo.length === 0) { toast.error('Ingrese al menos un conteo'); return; }
    setSaving(true);
    try {
      const items = itemsConConteo.map(codigo => {
        const mat = materiales.find(m => m.codigo === codigo);
        return {
          codigo_material: codigo,
          stock_sistema: mat?.stock_actual || 0,
          stock_fisico: Number(conteos[codigo]),
        };
      });
      const res = await api.post('/bodega/inventario-fisico', { nombre, items });
      toast.success(res.data.message);
      setConteos({});
      setNombre('');
      loadMateriales();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    } finally { setSaving(false); }
  };

  const familias = [...new Set(materiales.map(m => m.familia_nombre).filter(Boolean))].sort();

  return (
    <div className="space-y-6" data-testid="inventario-fisico-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ClipboardCheck className="h-6 w-6" /> Inventario Físico
          </h1>
          <p className="text-muted-foreground text-sm mt-1">Conteo y ajuste de stock</p>
        </div>
        <div className="flex gap-2">
          <Button variant={tab === 'conteo' ? 'default' : 'outline'} onClick={() => setTab('conteo')} data-testid="tab-conteo">
            <ClipboardCheck className="h-4 w-4 mr-1" /> Nuevo Conteo
          </Button>
          <Button variant={tab === 'historial' ? 'default' : 'outline'} onClick={() => setTab('historial')} data-testid="tab-historial">
            <History className="h-4 w-4 mr-1" /> Historial
          </Button>
        </div>
      </div>

      {tab === 'conteo' && (
        <div className="space-y-4">
          {/* Filters and name */}
          <div className="bg-card border rounded-xl p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              <Input placeholder="Nombre inventario (ej: Conteo EPP Abril)" value={nombre}
                onChange={e => setNombre(e.target.value)} data-testid="nombre-inventario" />
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input className="pl-9" placeholder="Buscar material..." value={search}
                  onChange={e => setSearch(e.target.value)} data-testid="search-inventario" />
              </div>
              <select className="h-9 rounded-md border bg-background px-3 text-sm"
                value={familiaFilter} onChange={e => setFamiliaFilter(e.target.value)} data-testid="familia-filter">
                <option value="">Todas las familias</option>
                {familias.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
              <div className="flex items-center gap-3 text-sm">
                <span className="text-muted-foreground">Contados: <strong>{itemsConConteo.length}</strong></span>
                <span className="text-amber-600">Diferencias: <strong>{itemsConDiferencia.length}</strong></span>
              </div>
            </div>
          </div>

          {/* Counting table */}
          <div className="bg-card border rounded-xl overflow-hidden">
            {loading ? (
              <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="inventario-table">
                  <thead><tr className="border-b bg-muted/50">
                    <th className="text-left px-4 py-3 font-medium">Material</th>
                    <th className="text-left px-4 py-3 font-medium">Familia</th>
                    <th className="text-right px-4 py-3 font-medium">Stock Sistema</th>
                    <th className="text-center px-4 py-3 font-medium w-32">Conteo Físico</th>
                    <th className="text-right px-4 py-3 font-medium">Diferencia</th>
                  </tr></thead>
                  <tbody>
                    {filteredMats.map((mat, idx) => {
                      const fisico = conteos[mat.codigo];
                      const hasCont = fisico !== undefined && fisico !== '';
                      const diff = hasCont ? Number(fisico) - mat.stock_actual : null;
                      return (
                        <tr key={idx} className={`border-b ${diff && diff !== 0 ? 'bg-amber-50/50 dark:bg-amber-950/10' : ''}`}
                          data-testid={`inv-row-${idx}`}>
                          <td className="px-4 py-2">
                            <div className="font-medium text-xs">{mat.descripcion2}</div>
                            <div className="text-[10px] text-muted-foreground font-mono">{mat.codigo}</div>
                          </td>
                          <td className="px-4 py-2 text-xs text-muted-foreground">{mat.familia_nombre}</td>
                          <td className="px-4 py-2 text-right font-mono">{mat.stock_actual}</td>
                          <td className="px-4 py-2">
                            <Input type="number" min="0" className="w-24 mx-auto text-center h-8 text-sm"
                              value={conteos[mat.codigo] ?? ''}
                              onChange={e => setConteo(mat.codigo, e.target.value)}
                              placeholder="-" />
                          </td>
                          <td className="px-4 py-2 text-right">
                            {diff !== null && diff !== 0 ? (
                              <span className={`font-bold ${diff > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                {diff > 0 ? '+' : ''}{diff}
                              </span>
                            ) : diff === 0 ? (
                              <CheckCircle className="h-4 w-4 text-emerald-500 ml-auto" />
                            ) : null}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Submit */}
          {itemsConConteo.length > 0 && (
            <div className="bg-card border rounded-xl p-4 flex items-center justify-between">
              <div className="text-sm">
                <strong>{itemsConConteo.length}</strong> items contados, <strong className="text-amber-600">{itemsConDiferencia.length}</strong> con diferencia
              </div>
              <Button size="lg" disabled={saving} onClick={submitInventario} data-testid="submit-inventario">
                {saving ? <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                Registrar Inventario y Ajustar Stock
              </Button>
            </div>
          )}
        </div>
      )}

      {tab === 'historial' && (
        <div className="space-y-4">
          {loading ? (
            <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>
          ) : historial.length === 0 ? (
            <div className="bg-card border rounded-xl p-12 text-center text-muted-foreground">Sin inventarios registrados</div>
          ) : (
            historial.map((inv, idx) => (
              <div key={idx} className="bg-card border rounded-xl overflow-hidden" data-testid={`inv-hist-${idx}`}>
                <button className="w-full text-left px-4 py-3 flex items-center justify-between hover:bg-muted/30"
                  onClick={() => setExpandedInv(expandedInv === idx ? null : idx)}>
                  <div>
                    <div className="font-semibold text-sm">{inv.nombre}</div>
                    <div className="text-xs text-muted-foreground">
                      {new Date(inv.fecha).toLocaleDateString('es-CL')} | {inv.total_items} items | {inv.items_con_diferencia} con diferencia | Por: {inv.realizado_por}
                    </div>
                  </div>
                  <ArrowUpDown className="h-4 w-4 text-muted-foreground" />
                </button>
                {expandedInv === idx && (
                  <div className="border-t">
                    <table className="w-full text-xs">
                      <thead><tr className="bg-muted/30">
                        <th className="text-left px-4 py-2">Material</th>
                        <th className="text-right px-4 py-2">Sistema</th>
                        <th className="text-right px-4 py-2">Físico</th>
                        <th className="text-right px-4 py-2">Diferencia</th>
                      </tr></thead>
                      <tbody>
                        {inv.items.filter(i => i.diferencia !== 0).map((item, ii) => (
                          <tr key={ii} className="border-t">
                            <td className="px-4 py-1.5">{item.descripcion}</td>
                            <td className="px-4 py-1.5 text-right">{item.stock_sistema}</td>
                            <td className="px-4 py-1.5 text-right">{item.stock_fisico}</td>
                            <td className="px-4 py-1.5 text-right">
                              <span className={`font-bold ${item.diferencia > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                {item.diferencia > 0 ? '+' : ''}{item.diferencia}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
